import React, { useState, useRef } from 'react';
import { Input, Select, Button, Collapse, message, Modal, Tooltip, Tag } from 'antd';
import { 
  QuestionCircleOutlined, 
  SendOutlined, 
  UploadOutlined, 
  CheckCircleFilled, 
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  PictureOutlined,
  SearchOutlined,
  CustomerServiceOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  MailOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { fullname, email, role } from '../utils/data';
import { SupportUrl, extractErrorMessage } from '../utils/network';
import { getAuthToken } from '../utils/functions';
import Header from '../components/Header';
import HelpSupportIcon from '../components/svg/HelpSupport';
import skyBg from '../assets/images/background/SkyWithClouds.png';
import skyBgMobile from '../assets/images/background/SkyWithCloudsMobile.png';
import rocksLeft from '../assets/images/background/RocksWithBird.png';
import rocksRight from '../assets/images/background/RocksWithGrass2.png';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface SupportTicketData {
  id?: number | string;
  email?: string;
  issue_type?: string;
  description?: string;
  screenshot?: string | null;
  created_at?: string;
  status?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
};

const FAQ_DATA: FAQItem[] = [
  {
    id: '1',
    category: 'General',
    question: 'What is EmployPro ?',
    answer: 'EmployPro is a confidential digital tool designed to help individuals privately explore, and understand potential workplace discrimination or conflict.'
  },
  {
    id: '2',
    category: 'Interviews',
    question: 'How do I start a new interview session?',
    answer: 'To begin a new interview, click on "Begin New Interview" on your dashboard. Follow the step-by-step interview wizard to input candidate details, configure scoring criteria, and launch the structured evaluation session.'
  },
  {
    id: '3',
    category: 'Interviews',
    question: 'How do I view or print past interview reports?',
    answer: 'Navigate to "View/Print an existing Interview" from the home dashboard. There you can search, filter by candidate or date, review detailed scoring breakdowns, and export or print complete interview PDF reports.'
  },
  {
    id: '4',
    category: 'Billing',
    question: 'How do subscriptions and free trials work?',
    answer: 'EmployPro offers recurring plans with access to full case creation, custom scoring methods, and Clara AI Chatbot. You can manage, upgrade, or cancel your active subscription anytime in the "Subscription & Billing" section.'
  },
  {
    id: '5',
    category: 'Clara AI',
    question: 'What is Clara and how do I access AI Chatbot features?',
    answer: 'Clara is your automated AI assistant designed to help analyze candidate answers, suggest follow-up questions, and assist in drafting rubrics. Access to Clara is included with active subscription tiers.'
  },
  {
    id: '6',
    category: 'Technical',
    question: 'What image and file formats are supported for attachments?',
    answer: 'For screenshots and support ticket attachments, you can upload any image format (PNG, JPG, JPEG, WEBP, GIF, SVG, etc.) up to 5MB in size.'
  }
];

const SupportPage: React.FC = () => {
  const currentUser = {
    role: localStorage.getItem(role) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(email) || ''
  };

  // Form State
  const [userEmail, setUserEmail] = useState(currentUser.email);
  const [issueType, setIssueType] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<{ title: string; message: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [submittedTicketId, setSubmittedTicketId] = useState('');
  const [submittedTicketData, setSubmittedTicketData] = useState<SupportTicketData | null>(null);
  const [submittedMessage, setSubmittedMessage] = useState<string>('');

  // FAQ Filter State
  const [faqSearch, setFaqSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'General', 'Interviews', 'Billing', 'Clara AI', 'Technical'];

  const filteredFaqs = FAQ_DATA.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    const matchesSearch = 
      faq.question.toLowerCase().includes(faqSearch.toLowerCase()) || 
      faq.answer.toLowerCase().includes(faqSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear previous error
    setUploadError(null);

    const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|svg|tiff|heic|ico|avif)$/i.test(file.name);
    if (!isImage) {
      const errMsg = `"${file.name}" is not a supported image file. Please upload an image format (PNG, JPG, WEBP, etc.).`;
      setUploadError({
        title: 'Invalid File Format',
        message: errMsg
      });
      message.error(`${file.name} is not a valid image file.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const isLt5M = file.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      const errMsg = `"${file.name}" (${formatFileSize(file.size)}) exceeds the 5MB upload limit. Please choose a smaller image.`;
      setUploadError({
        title: 'File Size Limit Exceeded',
        message: errMsg
      });
      message.error('Image size must not exceed 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setUploadError(null);
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userEmail || !userEmail.trim()) {
      message.error('Please provide a valid email address.');
      return;
    }
    if (!issueType) {
      message.error('Please select an issue type.');
      return;
    }
    if (!description.trim()) {
      message.error('Please describe your issue or question.');
      return;
    }

    setIsSubmitting(true);

    try {
      const authHeaders = getAuthToken();
      const formData = new FormData();
      formData.append('email', userEmail.trim());
      formData.append('issue_type', issueType);
      formData.append('description', description.trim());

      if (selectedFile) {
        formData.append('screenshot', selectedFile, selectedFile.name);
      }

      const res = await axios.post(SupportUrl, formData, {
        headers: {
          ...(authHeaders?.headers || {}),
        },
      });

      const msg = res?.data?.message || 'Support ticket submitted successfully. Our support team will get back to you soon.';
      const data = res?.data?.data;

      setSubmittedMessage(msg);
      setSubmittedTicketData(data || null);
      setSubmittedTicketId(data?.id ? `#${data.id}` : `EP-${Math.floor(100000 + Math.random() * 900000)}`);

      // Toast notification
      message.success(msg);

      // Open confirmation modal / card
      setIsSuccessModalVisible(true);

      // Reset form
      setDescription('');
      setIssueType(undefined);
      handleRemoveFile();
    } catch (err: any) {
      const errorMsg = extractErrorMessage(err) || 'Failed to submit support ticket. Please try again.';
      message.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support-page-container relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['Lexend',sans-serif]">
      <style dangerouslySetInnerHTML={{
        __html: `
          .support-page-container,
          .support-page-container * {
            font-family: 'Lexend', sans-serif !important;
          }
          .support-page-select-dropdown .ant-select-item {
            font-family: 'Lexend', sans-serif !important;
            font-size: 14px !important;
            padding: 10px 14px !important;
          }
          .support-modal .ant-modal-content {
            font-family: 'Lexend', sans-serif !important;
          }
          .support-page-container .ant-upload-list-item-name {
            font-family: 'Lexend', sans-serif !important;
            font-size: 13px !important;
          }
        `
      }} />

      {/* Top Header */}
      <Header currentUser={currentUser} showAdmin={true} />

      {/* Main Content Area */}
      <main className="relative z-[5] flex-1 flex flex-col items-center px-4 sm:px-6 md:px-12 py-6 md:py-10 max-w-[1350px] w-full mx-auto font-['Lexend',sans-serif]">
        
        {/* Page Title & Hero */}
        <div className="text-center mb-8 md:mb-10 max-w-2xl">
          <div className="inline-flex items-center justify-center p-3.5 bg-white/80 backdrop-blur-md rounded-2xl shadow-sm mb-3">
            <HelpSupportIcon className="w-9 h-9 md:w-11 md:h-11" />
          </div>
          <h1 className="font-['Lexend',sans-serif] text-2xl sm:text-3xl md:text-4xl font-bold text-[#1f2a37] tracking-tight mb-2.5">
            Help & Support Desk
          </h1>
          <p className="font-['Lexend',sans-serif] text-sm sm:text-base text-[#4b5563] leading-relaxed">
            Need assistance with your account, interviews, or billing? Send our support team a message or explore answers to common questions below.
          </p>
        </div>

        {/* 2-Column Responsive Grid: FAQs FIRST (Left / Top), Contact Form SECOND (Right / Bottom) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full items-start mb-16">
          
          {/* 1. FIRST SECTION: Frequently Asked Questions (6 cols on lg) */}
          <div className="lg:col-span-6 w-full flex flex-col gap-6 font-['Lexend',sans-serif]">
            <div className="bg-white/95 backdrop-blur-md border border-white/80 rounded-[20px] shadow-[0px_8px_30px_rgba(0,0,0,0.06)] p-6 sm:p-7 md:p-8">
              
              {/* Card Header & Search */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 pb-4 mb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#4682B4]">
                    <QuestionCircleOutlined className="text-xl" />
                  </div>
                  <div>
                    <h2 className="font-['Lexend',sans-serif] text-lg sm:text-xl font-bold text-[#1f2a37] m-0">
                      Frequently Asked Questions
                    </h2>
                    <p className="font-['Lexend',sans-serif] text-xs sm:text-sm text-gray-500 m-0">Quick answers to common questions</p>
                  </div>
                </div>
                
                {/* Search FAQ */}
                <div className="w-full sm:w-52">
                  <Input
                    placeholder="Search FAQs..."
                    prefix={<SearchOutlined className="text-gray-400" />}
                    value={faqSearch}
                    onChange={e => setFaqSearch(e.target.value)}
                    allowClear
                    className="rounded-xl border-gray-200 text-sm py-2 font-['Lexend',sans-serif]"
                  />
                </div>
              </div>

              {/* Category Pills */}
              <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4 scrollbar-none font-['Lexend',sans-serif]">
                {categories.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-['Lexend',sans-serif] font-medium transition-all duration-200 border cursor-pointer shrink-0 ${
                      selectedCategory === cat
                        ? 'bg-[#4682B4] text-white border-[#4682B4] shadow-sm'
                        : 'bg-gray-50 text-[#555d67] border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* FAQ Accordion */}
              {filteredFaqs.length > 0 ? (
                <Collapse
                  defaultActiveKey={['1']}
                  expandIconPosition="end"
                  className="bg-transparent border-0 [&_.ant-collapse-item]:border [&_.ant-collapse-item]:border-gray-100 [&_.ant-collapse-item]:rounded-xl [&_.ant-collapse-item]:mb-3 [&_.ant-collapse-item]:overflow-hidden [&_.ant-collapse-header]:bg-[#f8fafc] [&_.ant-collapse-header]:font-['Lexend',sans-serif] [&_.ant-collapse-header]:font-semibold [&_.ant-collapse-header]:py-3.5 sm:[&_.ant-collapse-header]:py-4 [&_.ant-collapse-content-box]:p-4 sm:[&_.ant-collapse-content-box]:p-5 [&_.ant-collapse-content-box]:text-sm sm:[&_.ant-collapse-content-box]:text-[15px] [&_.ant-collapse-content-box]:text-[#4b5563] [&_.ant-collapse-content-box]:leading-relaxed font-['Lexend',sans-serif]"
                >
                  {filteredFaqs.map(faq => (
                    <Panel
                      header={
                        <div className="flex items-center gap-2.5 pr-2 font-['Lexend',sans-serif]">
                          <Tag color="blue" className="text-xs rounded-md border-0 bg-blue-50 text-[#4682B4] font-medium m-0 font-['Lexend',sans-serif] px-2 py-0.5">
                            {faq.category}
                          </Tag>
                          <span className="text-[#1f2a37] text-sm sm:text-base font-semibold leading-snug font-['Lexend',sans-serif]">{faq.question}</span>
                        </div>
                      }
                      key={faq.id}
                    >
                      <p className="m-0 text-sm sm:text-[15px] text-[#4b5563] leading-relaxed font-['Lexend',sans-serif]">{faq.answer}</p>
                    </Panel>
                  ))}
                </Collapse>
              ) : (
                <div className="py-10 text-center text-gray-400 font-['Lexend',sans-serif]">
                  <QuestionCircleOutlined className="text-3xl mb-2 text-gray-300" />
                  <p className="text-sm sm:text-base text-gray-500 m-0 font-['Lexend',sans-serif]">No matching questions found for "{faqSearch}".</p>
                  <Button size="middle" className="mt-3 rounded-xl text-sm font-['Lexend',sans-serif]" onClick={() => { setFaqSearch(''); setSelectedCategory('All'); }}>
                    Reset Filters
                  </Button>
                </div>
              )}

              {/* Help tip card */}
              <div className="mt-5 p-4 rounded-xl bg-blue-50/60 border border-blue-100 flex items-start gap-3 font-['Lexend',sans-serif]">
                <InfoCircleOutlined className="text-[#4682B4] text-base mt-0.5 shrink-0" />
                <div className="text-xs sm:text-sm text-[#334155] leading-relaxed font-['Lexend',sans-serif]">
                  <strong className="font-semibold text-[#1e293b]">Still have questions?</strong> Fill out the contact support form with your query and our team will get back to you.
                </div>
              </div>
            </div>
          </div>

          {/* 2. SECOND SECTION: Contact Support Form (6 cols on lg) */}
          <div className="lg:col-span-6 w-full">
            <div className="bg-white/95 backdrop-blur-md border border-white/80 rounded-[20px] shadow-[0px_8px_30px_rgba(0,0,0,0.06)] p-6 sm:p-7 md:p-8 font-['Lexend',sans-serif]">
              
              {/* Card Header */}
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-[#4682B4]">
                    <CustomerServiceOutlined className="text-xl" />
                  </div>
                  <div>
                    <h2 className="font-['Lexend',sans-serif] text-lg sm:text-xl font-bold text-[#1f2a37] m-0">
                      Contact Support
                    </h2>
                    <p className="font-['Lexend',sans-serif] text-xs sm:text-sm text-gray-500 m-0">We typically reply within 24 hours</p>
                  </div>
                </div>

                <Tag color="blue" className="rounded-full px-3 py-1 text-xs font-['Lexend',sans-serif] border-0 bg-blue-50 text-[#4682B4] font-medium hidden sm:inline-flex items-center gap-1.5">
                  <ClockCircleOutlined /> Fast Support
                </Tag>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-['Lexend',sans-serif]">
                
                {/* User Email */}
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5 font-['Lexend',sans-serif]">
                    Your Email Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={userEmail}
                    onChange={e => setUserEmail(e.target.value)}
                    placeholder="name@example.com"
                    prefix={<MailOutlined className="text-gray-400 mr-1.5" />}
                    required
                    className="rounded-xl py-2.5 px-3 text-sm md:text-[15px] font-['Lexend',sans-serif]"
                  />
                </div>

                {/* Issue Type */}
                <div>
                  <label className="block text-sm font-semibold text-[#374151] mb-1.5 font-['Lexend',sans-serif]">
                    Issue Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    placeholder="Select issue type"
                    value={issueType}
                    onChange={value => setIssueType(value)}
                    className="w-full text-sm md:text-[15px] font-['Lexend',sans-serif] [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!h-[44px] [&_.ant-select-selection-item]:!leading-[42px] [&_.ant-select-selection-placeholder]:!leading-[42px] [&_.ant-select-selection-item]:!text-sm md:[&_.ant-select-selection-item]:!text-[15px] [&_.ant-select-selection-placeholder]:!text-sm md:[&_.ant-select-selection-placeholder]:!text-[15px] [&_.ant-select-selection-item]:!font-['Lexend',sans-serif]"
                    popupClassName="support-page-select-dropdown rounded-xl font-['Lexend',sans-serif]"
                  >
                    <Option value="General Inquiry">General Inquiry</Option>
                    <Option value="Technical Issue">Technical Issue</Option>
                    <Option value="Billing & Subscription">Billing & Subscription</Option>
                    <Option value="Account Access">Account Access</Option>
                    <Option value="Feature Request">Feature Request</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </div>

                {/* Description with Clean Independent Header */}
                <div>
                  <div className="flex flex-wrap items-baseline justify-between gap-1 mb-1.5">
                    <label className="block text-sm font-semibold text-[#374151] font-['Lexend',sans-serif]">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500 font-normal font-['Lexend',sans-serif]">
                      {description.length} / 1500 characters
                    </span>
                  </div>
                  <TextArea
                    rows={4}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Please explain what happened or what assistance you need in detail..."
                    required
                    maxLength={1500}
                    className="rounded-xl text-sm md:text-[15px] p-3 border-gray-300 hover:border-[#4682B4] focus:border-[#4682B4] font-['Lexend',sans-serif]"
                  />
                </div>

                {/* Screenshot Upload (Choose File Section with Selected File Beside) */}
                <div className="pt-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-1 mb-1.5">
                    <label className="block text-sm font-semibold text-[#374151] font-['Lexend',sans-serif]">
                      Attach Screenshot <span className="text-gray-400 font-normal font-['Lexend',sans-serif]">(Optional)</span>
                    </label>
                    <span className="text-xs text-gray-500 font-['Lexend',sans-serif]">Image only (Max 5MB)</span>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    className="hidden"
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
                    <Button 
                      htmlType="button"
                      icon={<UploadOutlined className="text-[#4682B4] text-base" />} 
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-xl flex items-center justify-center gap-2 border border-dashed border-gray-300 hover:border-[#4682B4] py-2.5 px-4 h-auto text-sm font-medium text-gray-700 font-['Lexend',sans-serif] bg-gray-50/70 hover:bg-blue-50/30 transition-all shrink-0 cursor-pointer shadow-sm hover:text-[#4682B4]"
                    >
                      {selectedFile ? 'Change File' : 'Browse & Attach Screenshot'}
                    </Button>

                    {selectedFile ? (
                      <div className="flex items-center gap-2.5 bg-blue-50/90 border border-blue-200/90 rounded-xl px-3 py-2 flex-1 min-w-0 shadow-sm animate-fadeIn">
                        {previewUrl ? (
                          <img 
                            src={previewUrl} 
                            alt="Screenshot preview" 
                            className="w-8 h-8 rounded-lg object-cover border border-blue-200 shrink-0 bg-white shadow-xs"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-[#4682B4] shrink-0">
                            <PictureOutlined className="text-base" />
                          </div>
                        )}

                        <div className="flex flex-col min-w-0 flex-1">
                          <Tooltip title={selectedFile.name} placement="topLeft">
                            <span className="text-xs sm:text-sm font-medium text-gray-800 truncate font-['Lexend',sans-serif]">
                              {selectedFile.name}
                            </span>
                          </Tooltip>
                          <span className="text-[11px] text-gray-500 font-['Lexend',sans-serif]">
                            {formatFileSize(selectedFile.size)}
                          </span>
                        </div>

                        <Tooltip title="Remove screenshot">
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors border-0 bg-transparent cursor-pointer shrink-0 flex items-center justify-center"
                            aria-label="Remove screenshot"
                          >
                            <DeleteOutlined className="text-sm" />
                          </button>
                        </Tooltip>
                      </div>
                    ) : (
                      <div className="flex items-center text-xs sm:text-sm text-gray-400 italic px-1 font-['Lexend',sans-serif]">
                        No file chosen
                      </div>
                    )}
                  </div>

                  {/* Upload Error Occurred Card */}
                  {uploadError && (
                    <div className="mt-2.5 p-3 rounded-xl bg-red-50/95 border border-red-200/90 flex items-start justify-between gap-3 shadow-sm animate-fadeIn font-['Lexend',sans-serif]">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <CloseCircleFilled className="text-red-500 text-base sm:text-lg mt-0.5 shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs sm:text-sm font-semibold text-red-800 leading-tight">
                            {uploadError.title}
                          </span>
                          <span className="text-xs text-red-600 mt-0.5 leading-relaxed font-normal">
                            {uploadError.message}
                          </span>
                        </div>
                      </div>
                      <Tooltip title="Dismiss error">
                        <button
                          type="button"
                          onClick={() => setUploadError(null)}
                          className="p-1 rounded-lg text-red-400 hover:text-red-700 hover:bg-red-100 transition-colors border-0 bg-transparent cursor-pointer shrink-0 flex items-center justify-center"
                          aria-label="Dismiss error"
                        >
                          <CloseOutlined className="text-xs" />
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    icon={<SendOutlined />}
                    className="w-full h-12 bg-gradient-to-r from-[#4682B4] to-[#6FB6F2] hover:from-[#3a6d96] hover:to-[#5ba3df] border-0 rounded-xl font-['Lexend',sans-serif] font-semibold text-sm sm:text-base shadow-[0_4px_14px_rgba(70,130,180,0.25)] transition-all duration-200"
                  >
                    Submit Support Ticket
                  </Button>
                </div>
              </form>
            </div>
          </div>

        </div>
      </main>

      {/* Success Modal Confirmation */}
      <Modal
        open={isSuccessModalVisible}
        onOk={() => setIsSuccessModalVisible(false)}
        onCancel={() => setIsSuccessModalVisible(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setIsSuccessModalVisible(false)}
            className="bg-[#4682B4] hover:bg-[#3a6d96] rounded-xl px-8 h-11 font-['Lexend',sans-serif] font-semibold text-sm"
          >
            Done
          </Button>
        ]}
        centered
        className="support-modal [&_.ant-modal-content]:rounded-2xl font-['Lexend',sans-serif]"
      >
        <div className="text-center py-4 font-['Lexend',sans-serif]">
          <CheckCircleFilled className="text-5xl text-emerald-500 mb-3" />
          <h3 className="font-['Lexend',sans-serif] text-xl sm:text-2xl font-bold text-[#1f2a37] mb-2">
            Ticket Submitted Successfully!
          </h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 font-['Lexend',sans-serif]">
            {submittedMessage || `Thank you for reaching out. We have received your inquiry and our support team will respond to ${userEmail} shortly.`}
          </p>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 w-full text-left font-['Lexend',sans-serif] flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Ticket Reference</span>
              <span className="font-mono text-base font-bold text-[#4682B4]">{submittedTicketId}</span>
            </div>
            {submittedTicketData?.issue_type && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Issue Type:</span>
                <span className="font-semibold text-gray-800">{submittedTicketData.issue_type}</span>
              </div>
            )}
            {submittedTicketData?.status && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 font-medium">Status:</span>
                <Tag color="green" className="m-0 font-['Lexend',sans-serif] rounded-md font-semibold">{submittedTicketData.status}</Tag>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Bottom Assets */}
      <div className="relative w-full pointer-events-none z-[2] flex justify-between items-end mt-auto">
        <img src={rocksLeft} alt="" className="w-[209px] md:w-[220px] lg:w-[250px] xl:w-[280px] h-auto object-contain object-left-bottom" />
        <img src={rocksRight} alt="" className="w-[110px] md:w-[150px] lg:w-[180px] xl:w-[210px] h-auto object-contain object-right-bottom" />
      </div>

      {/* Background Assets */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover md:min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>
    </div>
  );
};

export default SupportPage;

